import * as XLSX from 'xlsx'

import { DISTRIBUTION_TEMPLATE_FILENAME, DISTRIBUTION_TEMPLATE_HEADER } from 'constants/promo-code'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value)

/**
 * Extract emails from an uploaded xlsx/xls/csv whitelist file (first sheet,
 * any column). Invalid values and the template header are skipped; duplicates
 * are removed case-insensitively.
 */
export const parseEmailsFromFile = async (file: File): Promise<string[]> => {
  const workbook = XLSX.read(await file.arrayBuffer())
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return []

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 })
  const seen = new Set<string>()
  const emails: string[] = []
  for (const row of rows) {
    for (const cell of row ?? []) {
      const value = String(cell ?? '').trim()
      if (!isValidEmail(value)) continue
      const key = value.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      emails.push(value)
    }
  }
  return emails
}

/**
 * Rebuild the whitelist xlsx from the emails the admin kept after upload
 * (some may have been removed in the UI), so the uploaded file always matches
 * what is shown on screen.
 */
export const buildWhitelistFile = (fileName: string, emails: string[]): File => {
  const sheet = XLSX.utils.aoa_to_sheet([
    [DISTRIBUTION_TEMPLATE_HEADER],
    ...emails.map((email) => [email]),
  ])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'whitelist')
  const output = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
  const name = fileName.replace(/\.(xlsx|xls|csv)$/i, '') + '.xlsx'
  return new File([output], name, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

/**
 * Build the whitelist template CSV in-memory and trigger a browser download.
 * Generated client-side (no static asset) so it works identically on dev and prod.
 */
export const downloadWhitelistTemplate = () => {
  const csv = [DISTRIBUTION_TEMPLATE_HEADER, 'nam.nguyen@meeyland.com'].join('\n')
  // Prepend a BOM so Excel opens the UTF-8 CSV with correct encoding.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = DISTRIBUTION_TEMPLATE_FILENAME
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
